<template>
  <div class="nav-root">
    <header class="na-nav" :class="{ 'na-nav-scrolled': isScrolled }">
      <div class="na-wrap nav-row">
        <router-link to="/" class="nav-brand" aria-label="NewsAppify home">
          <span class="na-seal">N</span>
          <span class="nav-brand-text">
            <span class="na-wordmark">News<span>Appify</span></span>
            <span class="na-tagline">Your town. Mobilized.</span>
          </span>
        </router-link>

        <nav class="nav-links" aria-label="Main">
          <router-link v-for="l in links" :key="l.to" :to="l.to" class="nav-link" :exact="l.to === '/'">{{ l.label }}</router-link>
        </nav>

        <div class="nav-actions">
          <router-link to="/login" class="na-btn na-btn-outline nav-btn">
            <v-icon size="19">mdi-login</v-icon>Login
          </router-link>
          <router-link to="/demo" class="na-btn na-btn-primary nav-btn">
            Request a Demo<v-icon size="19">mdi-arrow-right</v-icon>
          </router-link>
        </div>

        <button class="nav-burger" type="button" aria-label="Open menu" :aria-expanded="String(drawer)" @click="drawer = true">
          <v-icon color="#14315E">mdi-menu</v-icon>
        </button>
      </div>
    </header>

    <!-- Mobile drawer — a sibling of <header> on purpose: the header's
         backdrop-filter would otherwise trap this fixed panel inside it. -->
    <transition name="drawer">
      <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
        <div class="drawer-panel" role="dialog" aria-label="Menu" @click.stop>
          <div class="drawer-head">
            <router-link to="/" class="nav-brand" @click.native="drawer = false">
              <span class="na-seal">N</span>
              <span class="na-wordmark">News<span>Appify</span></span>
            </router-link>
            <button class="drawer-close" type="button" aria-label="Close menu" @click="drawer = false">
              <v-icon color="#14315E">mdi-close</v-icon>
            </button>
          </div>
          <nav class="drawer-links" aria-label="Main">
            <router-link v-for="l in links" :key="l.to" :to="l.to" class="drawer-link" :exact="l.to === '/'" @click.native="drawer = false">
              <v-icon size="20">{{ l.icon }}</v-icon>{{ l.label }}
            </router-link>
          </nav>
          <div class="drawer-actions">
            <router-link to="/demo" class="na-btn na-btn-primary na-btn-lg" @click.native="drawer = false">Request a Demo<v-icon size="19">mdi-arrow-right</v-icon></router-link>
            <router-link to="/login" class="na-btn na-btn-outline na-btn-lg" @click.native="drawer = false"><v-icon size="19">mdi-login</v-icon>Login</router-link>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import '@/assets/na-theme.css'

export default {
  name: 'AppNavigation',
  data: () => ({
    drawer: false,
    isScrolled: false,
    links: [
      { to: '/', label: 'Home', icon: 'mdi-home-outline' },
      { to: '/why', label: 'Why NewsAppify', icon: 'mdi-compass-outline' },
      { to: '/features', label: 'Features', icon: 'mdi-view-grid-outline' },
      { to: '/advertising', label: 'Advertising', icon: 'mdi-bullhorn-outline' },
      { to: '/about', label: 'About', icon: 'mdi-information-outline' },
      { to: '/blog', label: 'Blog', icon: 'mdi-post-outline' },
    ],
  }),
  watch: {
    drawer(open) { document.body.style.overflow = open ? 'hidden' : '' },
    $route() { this.drawer = false },
  },
  mounted() {
    this.onScroll()
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('keydown', this.onKey)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('keydown', this.onKey)
    document.body.style.overflow = ''
  },
  methods: {
    onScroll() { this.isScrolled = window.scrollY > 12 },
    onKey(e) { if (e.key === 'Escape') this.drawer = false },
  },
}
</script>

<style scoped>
.nav-root { display: contents; }
.na-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(245, 241, 232, 0.88);
  backdrop-filter: blur(14px) saturate(1.2);
  -webkit-backdrop-filter: blur(14px) saturate(1.2);
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.na-nav-scrolled { border-bottom-color: var(--na-rule); box-shadow: 0 8px 30px -18px rgba(16, 27, 45, 0.35); }
.nav-row { display: flex; align-items: center; justify-content: space-between; gap: 24px; height: 88px; }

.nav-brand { display: inline-flex; align-items: center; gap: 12px; text-decoration: none; }
.nav-brand-text { display: flex; flex-direction: column; }

.nav-links { display: flex; align-items: center; gap: 30px; }
.nav-link {
  position: relative;
  padding: 8px 0;
  font-family: var(--na-sans);
  font-weight: 500;
  font-size: 1.02rem;
  color: var(--na-ink-2);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.15s ease;
}
.nav-link:hover { color: var(--na-ink); }
.nav-link.router-link-active { color: var(--na-ink); font-weight: 600; }
.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 2.5px;
  background: var(--na-navy);
}

.nav-actions { display: flex; align-items: center; gap: 12px; }
.nav-btn { min-height: 48px; padding: 0 20px; }

.nav-burger {
  display: none;
  width: 46px;
  height: 46px;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--na-rule);
  border-radius: 6px;
  background: var(--na-card);
  cursor: pointer;
}

.drawer-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(11, 30, 59, 0.5); }
.drawer-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(350px, 88vw);
  background: var(--na-paper);
  display: flex;
  flex-direction: column;
  padding: 20px 22px 26px;
  overflow-y: auto;
  box-shadow: -24px 0 60px rgba(11, 30, 59, 0.25);
}
.drawer-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.drawer-close {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--na-rule);
  border-radius: 6px;
  background: var(--na-card);
  cursor: pointer;
}
.drawer-links { display: flex; flex-direction: column; border-top: 1px solid var(--na-rule); }
.drawer-link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 17px 4px;
  border-bottom: 1px solid var(--na-rule);
  font-family: var(--na-serif);
  font-weight: 600;
  font-size: 1.2rem;
  color: var(--na-ink);
  text-decoration: none;
}
.drawer-link ::v-deep .v-icon { color: var(--na-brass-text) !important; }
.drawer-link.router-link-active { color: var(--na-navy); }
.drawer-actions { display: flex; flex-direction: column; gap: 10px; margin-top: auto; padding-top: 28px; }

.drawer-enter-active, .drawer-leave-active { transition: opacity 0.2s ease; }
.drawer-enter-active .drawer-panel, .drawer-leave-active .drawer-panel { transition: transform 0.25s ease; }
.drawer-enter, .drawer-leave-to { opacity: 0; }
.drawer-enter .drawer-panel, .drawer-leave-to .drawer-panel { transform: translateX(100%); }

@media (max-width: 1120px) { .nav-links { gap: 20px; } .nav-link { font-size: 0.96rem; } }
@media (max-width: 1020px) {
  .nav-links, .nav-actions { display: none; }
  .nav-burger { display: flex; }
  .nav-row { height: 76px; }
}
@media (max-width: 400px) { .na-tagline { display: none; } }
</style>